import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";

type SliderVisualState = "idle" | "loading" | "success";

@Component({
  selector: "app-slide-to-pay",
  templateUrl: "./slide-to-pay.component.html",
  styleUrls: ["./slide-to-pay.component.scss"],
  standalone: true,
  imports: [TranslatePipe, CommonModule],
})
export class SlideToPayComponent implements AfterViewInit, OnDestroy {
  @Input() disabled = false;
  @Input() buttonTextKey = "checkout_try_again.swipe_to_buy";

  @Output() activated = new EventEmitter<void>();

  @ViewChild("sliderButton", { static: false }) sliderButton!: ElementRef<HTMLElement>;
  @ViewChild("sliderContainer", { static: false }) sliderContainer!: ElementRef<HTMLElement>;

  sliderPosition = 5;
  isDragging = false;
  isActivated = false;
  sliderWidth = 0;
  containerWidth = 0;
  dragStartX = 0;

  visualState: SliderVisualState = "idle";

  private listeners: Array<() => void> = [];

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    this.initializeSlider();
  }

  ngOnDestroy(): void {
    this.removeListeners();
  }

  // Public API
  reset(): void {
    this.resetSlider();
  }

  showLoading(): void {
    this.showLoadingState();
  }

  showSuccess(): void {
    this.showSuccessState();
  }

  // Core slider setup
  initializeSlider(): void {
    if (!this.sliderButton || !this.sliderContainer) {
      return;
    }

    const sliderEl = this.sliderButton.nativeElement;
    const containerEl = this.sliderContainer.nativeElement;

    this.sliderWidth = sliderEl.offsetWidth;
    this.containerWidth = containerEl.offsetWidth;

    this.sliderPosition = 5;
    this.updateSliderPosition(this.sliderPosition);

    this.removeListeners();

    // Touch events
    this.listeners.push(
      this.renderer.listen(sliderEl, "touchstart", (event: TouchEvent) => this.handleTouchStart(event)),
      this.renderer.listen(sliderEl, "touchmove", (event: TouchEvent) => this.handleTouchMove(event)),
      this.renderer.listen(sliderEl, "touchend", () => this.handleTouchEnd())
    );

    // Mouse events
    this.listeners.push(
      this.renderer.listen(sliderEl, "mousedown", (event: MouseEvent) => this.handleMouseDown(event)),
      this.renderer.listen(document, "mousemove", (event: MouseEvent) => this.handleMouseMove(event)),
      this.renderer.listen(document, "mouseup", () => this.handleMouseUp())
    );
  }

  // Event handlers
  handleTouchStart(event: TouchEvent): void {
    if (this.disabled) return;
    this.startDrag(event.touches[0].clientX);
  }

  handleTouchMove(event: TouchEvent): void {
    if (this.disabled) return;
    this.drag(event.touches[0].clientX);
  }

  handleTouchEnd(): void {
    this.endDrag();
  }

  handleMouseDown(event: MouseEvent): void {
    if (this.disabled) return;
    this.startDrag(event.clientX);
  }

  handleMouseMove(event: MouseEvent): void {
    this.drag(event.clientX);
  }

  handleMouseUp(): void {
    this.endDrag();
  }

  // Drag logic
  private startDrag(startX: number): void {
    if (this.isActivated || this.disabled) return;
    this.isDragging = true;
    this.dragStartX = startX;
  }

  private drag(currentX: number): void {
    if (!this.isDragging || this.isActivated || this.disabled) return;

    const deltaX = currentX - this.dragStartX;
    const maxOffset = this.getMaxOffset();
    const newX = Math.max(5, Math.min(deltaX + 5, maxOffset));

    this.sliderPosition = newX;
    this.updateSliderPosition(this.sliderPosition);
  }

  private endDrag(): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    const maxOffset = this.getMaxOffset();

    if (this.sliderPosition >= maxOffset - 10 && !this.isActivated && !this.disabled) {
      this.activateSlider();
    } else {
      this.resetSlider();
    }
  }

  // Helpers
  private getMaxOffset(): number {
    if (!this.containerWidth || !this.sliderWidth) {
      return 250; // fallback similar to original implementation
    }
    return this.containerWidth - this.sliderWidth - 5;
  }

  activateSlider(): void {
    this.isActivated = true;
    this.visualState = "loading";
    const maxOffset = this.getMaxOffset();
    this.sliderPosition = maxOffset;
    this.updateSliderPosition(this.sliderPosition);
    this.activated.emit();
  }

  resetSlider(): void {
    this.isDragging = false;
    this.isActivated = false;
    this.visualState = "idle";
    this.sliderPosition = 5;
    this.updateSliderPosition(this.sliderPosition);
  }

  updateSliderPosition(position: number): void {
    if (!this.sliderButton) return;
    this.sliderButton.nativeElement.style.transform = `translateX(${position}px)`;
  }

  showSuccessState(): void {

    this.visualState = "success";

    console.info("showSuccessState", this.visualState);
  }

  showLoadingState(): void {
    if (!this.isActivated) {
      this.isActivated = true;
    }
    this.visualState = "loading";
  }

  private removeListeners(): void {
    this.listeners.forEach((unlisten) => {
      try {
        unlisten();
      } catch {
        // ignore
      }
    });
    this.listeners = [];
  }
}


